import { CollectionSearchInput } from '../ui/CollectionDatalist'
import { useMemo, useState } from 'react'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import {
  layerOptionState,
  rankLayeringCandidates,
  toggleLayerOptionSelection,
} from '../../lib/layeringPicker'
import { LAYERING_MODE_BEST } from '../../lib/layeringModes'
import PairingSuggestionPanel from './PairingSuggestionPanel'
import LayeringModeToggle from './LayeringModeToggle'
import OwnedOilSelector from './OwnedOilSelector'

export default function LayeringPicker({
  anchor,
  recommendationState,
  collection = [],
  logs = [],
  pairingRecommendations = [],
  unownedPairingRecommendations = [],
  pairingsLoading = false,
  pairingsError = '',
  selected = [],
  onChange,
  fullHeight = false,
}) {
  const layeringAdvisorModesEnabled = useFeatureFlag('layering_advisor_modes')
  const [query, setQuery] = useState('')
  const [rankingMode, setRankingMode] = useState(LAYERING_MODE_BEST)
  const [rankingSessionSeed] = useState(() => `${Date.now()}:${Math.random()}`)
  const ranked = useMemo(() => rankLayeringCandidates({
    collection,
    logs,
    anchorId: anchor?.id,
    query,
  }), [collection, logs, anchor?.id, query])
  const resultsClassName = fullHeight
    ? 'mt-3 rounded-xl border border-white/10 bg-charcoal-700'
    : 'mt-3 max-h-96 overflow-y-auto rounded-xl border border-white/10 bg-charcoal-700'

  const toggle = (option) => {
    const nextSelected = toggleLayerOptionSelection({ option, anchor, selected })
    if (nextSelected !== selected) onChange(nextSelected)
  }

  return (
    <section className={fullHeight ? 'flex min-h-0 flex-1 flex-col' : ''} data-layering-picker data-anchor-collection-id={anchor?.id || ''}>
      <div className="shrink-0" data-layering-controls>
        <div className="rounded-xl border border-gold/25 bg-gold/5 px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gold/70">Scent of the Day</p>
          <h3 className="mt-0.5 text-sm font-semibold text-cream">{anchor?.name}</h3>
          <p className="mt-0.5 text-[11px] text-cream/40">{anchor?.house}</p>
        </div>

        {layeringAdvisorModesEnabled && (
          <div className="mt-3" data-layering-sheet-modes>
            <LayeringModeToggle mode={rankingMode} onModeChange={setRankingMode} />
          </div>
        )}

        {selected.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Selected layers">
            {selected.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => toggle(item)}
                className="pill border border-gold/25 px-2.5 py-1 text-xs text-gold/80"
              >
                {item.name} ×
              </button>
            ))}
          </div>
        )}

      </div>

      <div className={fullHeight ? 'mt-3 min-h-0 flex-1 overflow-y-auto pr-1' : ''} data-layering-scroll-region>
        <PairingSuggestionPanel
          fragrance={anchor}
          recommendationState={recommendationState}
          collection={collection}
          fallbackPairings={pairingRecommendations}
          fallbackUnownedPairings={unownedPairingRecommendations}
          fallbackLoading={pairingsLoading}
          fallbackError={pairingsError}
          selectable
          selected={selected}
          onSelectedChange={onChange}
          rankingMode={layeringAdvisorModesEnabled ? rankingMode : undefined}
          rankingSessionSeed={rankingSessionSeed}
        />

        <OwnedOilSelector
          anchor={anchor}
          collection={collection}
          selected={selected}
          onSelectedChange={onChange}
        />

        <section className="mt-4" data-layering-collection>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-cream/55">Add another layer (oils included)</h3>
          <label className="mt-2 block text-xs text-cream/45" htmlFor="layer-collection-search">
            Search your collection
          </label>
          <CollectionSearchInput
            collection={rankLayeringCandidates({ collection, anchorId: anchor?.id }).map(({ item }) => item)}
            useNativeDatalist={false}
            onSelect={item => { toggle(item); setQuery('') }}
            id="layer-collection-search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Fragrance or oil"
            className="mt-1.5 w-full rounded-lg border border-white/8 bg-charcoal-700 px-3 py-2.5 text-sm text-cream outline-none placeholder:text-cream/30 focus:border-gold/40"
          />

          {ranked.length === 0 ? (
            <div className="mt-3 rounded-xl border border-dashed border-white/12 bg-charcoal-900/35 px-4 py-5 text-center" data-layering-empty-state>
              <p className="text-sm font-semibold text-cream/70">No eligible partners in your collection yet.</p>
              <p className="mt-1 text-xs leading-relaxed text-cream/40">Add a fragrance or oil to unlock layering choices. Pheromone items stay hidden.</p>
            </div>
          ) : (
            <>
              {ranked.length <= 2 && (
                <p className="mt-3 rounded-lg border border-sage/20 bg-sage/5 px-3 py-2 text-[11px] leading-relaxed text-sage" data-layering-thin-state>
                  Small collection, clear choices. Every option here is already yours.
                </p>
              )}
              <div className={resultsClassName} data-layering-options>
                {ranked.map(({ item, ownRating, wear, isOil }, index) => {
                  const state = layerOptionState({ option: item, anchor, selected })
                  return (
                    <button
                      key={`${item.id}-${item.concentration || 'none'}-${index}`}
                      type="button"
                      disabled={state.disabled}
                      aria-pressed={state.selected}
                      data-collection-row-id={item.id}
                      data-collection-binding={`${item.user_id}:${item.id}`}
                      data-concentration={item.concentration || ''}
                      onClick={() => toggle(item)}
                      className={`block w-full border-b border-white/5 px-3 text-left last:border-b-0 ${fullHeight ? 'py-2 sm:py-3' : 'py-3'} ${state.disabled ? 'cursor-not-allowed bg-white/[0.02] text-cream/25' : state.selected ? 'bg-gold/10 text-gold' : 'text-cream/80 hover:bg-white/5'}`}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{item.name}</span>
                          <span className="mt-0.5 block text-xs text-cream/40">{item.house}{item.concentration ? ` · ${item.concentration}` : ''}</span>
                        </span>
                        <span className="shrink-0 text-[10px] text-sage">
                          {ownRating > 0 ? `Your ${ownRating.toFixed(1)} ★` : `${wear.count} wear${wear.count === 1 ? '' : 's'}`}
                        </span>
                      </span>
                      {state.reason && <span className="mt-1.5 block text-[11px] text-amber-200" role="note">{state.reason}</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}
